import { FileText, Code2, ShieldCheck, FolderClosed, Mic, type LucideIcon } from 'lucide-react';

import type { PracticeCategoryKey, PracticeGroup, PracticeItem, PracticeRung } from '@/lib/api/lms';

/** Per-category visual identity, matching the Practice hub design. */
export interface CategoryMeta {
  key: PracticeCategoryKey;
  label: string;
  icon: LucideIcon;
  bg: string;
  fg: string;
}

export const CATEGORY_META: Record<PracticeCategoryKey, CategoryMeta> = {
  assignments: { key: 'assignments', label: 'Assignments', icon: FileText, bg: '#fff3ee', fg: '#ff5a1f' },
  problems: { key: 'problems', label: 'Problems', icon: Code2, bg: '#f5f3ff', fg: '#8b5cf6' },
  quizzes: { key: 'quizzes', label: 'Assessments', icon: ShieldCheck, bg: '#dcfce7', fg: '#16a34a' },
  projects: { key: 'projects', label: 'Projects', icon: FolderClosed, bg: '#fff7ed', fg: '#f59e0b' },
  viva: { key: 'viva', label: 'Viva', icon: Mic, bg: '#e0f2fe', fg: '#0284c7' },
};

/**
 * Display order on the hub (matches the Figma: Assignments, Problems,
 * Assessments, Projects), with Viva last — it is the one category a student
 * cannot act on from here, so it sits after everything they can.
 */
export const CATEGORY_ORDER: PracticeCategoryKey[] = ['assignments', 'problems', 'quizzes', 'projects', 'viva'];

/** Action-oriented subtitle for a category tile, derived from pending count. */
export function categorySubtitle(key: PracticeCategoryKey, total: number, completed: number): string {
  const pending = Math.max(0, total - completed);
  switch (key) {
    case 'assignments':
      return pending > 0 ? `${pending} Pending` : 'All Submitted';
    case 'problems':
      return pending > 0 ? 'Continue Solving' : 'All Solved';
    case 'quizzes':
      return pending > 0 ? `${pending} Upcoming` : 'All Done';
    case 'projects':
      return pending > 0 ? `${pending} In Progress` : 'All Submitted';
    case 'viva':
      // Not "pending" and never "submitted": a viva is something a trainer
      // does with you, so the only honest states are awaiting and marked.
      return pending > 0 ? `${pending} To Be Assessed` : 'All Assessed';
  }
}

/**
 * A set and the items inside it. `items` comes from the same flat item list
 * everything else renders from, so a group can never show a total the list
 * cannot account for.
 */
export interface GroupedItems {
  group: PracticeGroup;
  items: PracticeItem[];
}

/**
 * Split a list into authored sets and loose items.
 *
 * `groups` supplies the order and the labels; anything with no `group_key` — or
 * a key the server did not describe — falls through to `loose` and renders
 * exactly as it did before sets existed. A set that ends up with no items in
 * this particular list (e.g. filtering to one category) is dropped, so an empty
 * card can never appear.
 *
 * `memberSource` defaults to `items`. Pass the full item list when `items` is
 * itself a subset (the pending rail): a set that surfaces because two of its
 * ten problems are outstanding should still show all ten, not a card claiming
 * the week is two problems long.
 */
export function partitionByGroup(
  items: PracticeItem[],
  groups: PracticeGroup[],
  memberSource: PracticeItem[] = items,
): {
  grouped: GroupedItems[];
  loose: PracticeItem[];
} {
  const known = new Set(groups.map((g) => g.key));
  const present = new Set<string>();
  const loose: PracticeItem[] = [];

  for (const item of items) {
    const key = item.group_key;
    if (!key || !known.has(key)) {
      loose.push(item);
      continue;
    }
    present.add(key);
  }

  const members = new Map<string, PracticeItem[]>();
  for (const item of memberSource) {
    const key = item.group_key;
    if (!key || !present.has(key)) continue;
    const bucket = members.get(key);
    if (bucket) bucket.push(item);
    else members.set(key, [item]);
  }

  const grouped: GroupedItems[] = [];
  for (const g of groups) {
    const inSet = members.get(g.key);
    if (inSet && inSet.length > 0) grouped.push({ group: g, items: inSet });
  }
  return { grouped, loose };
}

/** One rung of a set's ladder. */
export interface LadderRung {
  key: PracticeRung | 'easy' | 'medium' | 'hard';
  label: string;
  count: number;
  fg: string;
  bg: string;
}

/**
 * The real ladder: the 3 warm-up / 4 core / 2 stretch / 1 debug-this shape
 * every weekly set is authored to, in the order a student climbs it.
 */
const RUNG_LADDER: Omit<LadderRung, 'count'>[] = [
  { key: 'warm_up', label: 'warm-up', fg: '#16a34a', bg: '#dcfce7' },
  { key: 'core', label: 'core', fg: '#7c3aed', bg: '#ede9fe' },
  { key: 'stretch', label: 'stretch', fg: '#b45309', bg: '#fef3c7' },
  { key: 'debug', label: 'debug', fg: '#dc2626', bg: '#fee2e2' },
];

const DIFFICULTY_LADDER: Omit<LadderRung, 'count'>[] = [
  { key: 'easy', label: 'Easy', fg: '#16a34a', bg: '#dcfce7' },
  { key: 'medium', label: 'Medium', fg: '#b45309', bg: '#fef3c7' },
  { key: 'hard', label: 'Hard', fg: '#dc2626', bg: '#fee2e2' },
];

/**
 * The ladder breakdown for a set.
 *
 * When the set's problems carry an authored `rung`, that IS the ladder and it
 * is what the card shows — "3 warm-up · 4 core · 2 stretch · 1 debug" is a
 * specific thing the product promises, and counting difficulties only ever
 * approximated it.
 *
 * A set with no rungs at all (every legacy assessment) falls back to the
 * difficulty tally, which is the only signal it carries. A set with no signal
 * either way returns an empty ladder and the card omits the row rather than
 * inventing rungs.
 */
export function ladderBreakdown(items: PracticeItem[]): LadderRung[] {
  const rungCounts: Record<PracticeRung, number> = { warm_up: 0, core: 0, stretch: 0, debug: 0 };
  let anyRung = false;
  for (const item of items) {
    const r = item.rung;
    if (r && r in rungCounts) {
      rungCounts[r] += 1;
      anyRung = true;
    }
  }
  if (anyRung) {
    return RUNG_LADDER.map((rung) => ({ ...rung, count: rungCounts[rung.key as PracticeRung] })).filter(
      (r) => r.count > 0,
    );
  }

  const counts = { easy: 0, medium: 0, hard: 0 };
  for (const item of items) {
    const d = item.difficulty?.toLowerCase();
    if (d === 'easy' || d === 'medium' || d === 'hard') counts[d] += 1;
  }
  return DIFFICULTY_LADDER.map((rung) => ({
    ...rung,
    count: counts[rung.key as 'easy' | 'medium' | 'hard'],
  })).filter((r) => r.count > 0);
}

const DAY_MS = 24 * 60 * 60 * 1000;

export interface DueLabel {
  text: string;
  urgent: boolean; // urgent → orange; else muted
}

/** Human due label + urgency, relative to now. Returns null when there's no due date. */
export function dueLabel(dueDate?: string, now: Date = new Date()): DueLabel | null {
  if (!dueDate) return null;
  const due = new Date(dueDate);
  if (Number.isNaN(due.getTime())) return null;

  const startOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  const days = Math.round((startOfDay(due) - startOfDay(now)) / DAY_MS);

  if (days < 0) return { text: 'Overdue', urgent: true };
  if (days === 0) return { text: 'Due Today', urgent: true };
  if (days === 1) return { text: 'Due Tomorrow', urgent: true };
  if (days <= 7) return { text: `Due in ${days} Days`, urgent: false };
  return {
    text: `Due ${due.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}`,
    urgent: false,
  };
}

/** Full due date + time, e.g. "Tomorrow, 11:59 PM" — for the detail header/sidebar. */
export function dueDateTime(dueDate?: string, now: Date = new Date()): string | null {
  if (!dueDate) return null;
  const due = new Date(dueDate);
  if (Number.isNaN(due.getTime())) return null;
  const time = due.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
  const label = dueLabel(dueDate, now);
  if (label && (label.text === 'Due Today' || label.text === 'Due Tomorrow')) {
    return `${label.text.replace('Due ', '')}, ${time}`;
  }
  return `${due.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}, ${time}`;
}

/**
 * Where a practice item leads when clicked:
 *  - problems → the existing problem solver (by slug)
 *  - quizzes → the existing in-topic quiz route
 *  - assignments/projects/viva → the assignment/project/viva detail page
 */
export type ItemNav =
  | { kind: 'problem'; slug: string }
  | { kind: 'quiz'; topicId: string; assessmentId: string }
  | { kind: 'detail'; category: PracticeCategoryKey; assessmentId: string }
  | { kind: 'topic'; topicId: string };

export function itemNav(item: PracticeItem): ItemNav {
  switch (item.category) {
    case 'problems':
      return item.problem_slug
        ? { kind: 'problem', slug: item.problem_slug }
        : { kind: 'topic', topicId: item.topic_id };
    case 'quizzes':
      return { kind: 'quiz', topicId: item.topic_id, assessmentId: item.id };
    case 'assignments':
    case 'projects':
    case 'viva':
      // The same detail page. A viva has nothing to submit, so that page shows
      // the brief, the marking scheme and the mark — which is exactly what a
      // student wants before and after sitting one.
      return { kind: 'detail', category: item.category, assessmentId: item.id };
  }
}
